import React, { ReactElement, useEffect, useState, useContext, useRef } from 'react';
import useSnackbar from '../../../../hooks/useSnackbar';
import * as Sentry from '@sentry/react';
import {
  Box,
  Typography,
  Button,
  Grid2,
  InputBase,
  IconButton,
  Dialog,
  TextField,
  Tooltip,
  Switch,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Close, ErrorOutline } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  white,
  gray40,
  gray80,
  gray100,
  green50,
  red50,
  yellow50,
  orange50,
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  buttonTextIconLarge,
} from '../../../../constants/colors';
import {
  boldXxl,
  boldMd,
  spHeaderTitle,
  regularSm,
  regularMd,
  regularLg,
  regularXl,
  cardInnerColumnGutter,
} from '../../../../constants/location';
import SideBar from '../../../uiParts/menu/SideBar';
import { usePageLayout } from '../../../../hooks/usePageLayout';
import MetaTitle from '../../../utils/MetaTitle';
import { getCompanyDetail } from '../../../../api/firebase/firestore/company/read';
import {
  updateCompanyCrmId,
  updateCompanyEnableMaster,
  updateCompanyMasterSelfUpload,
  updateCompanyEnableDictionary,
  updateCompanyEnableExportTemplate,
  updateCompanyEnableCustomFunctions,
  updateCompanyStripeIds,
} from '../../../../api/firebase/firestore/company/update';
import {
  getEarliestExpiringCredit,
  hasOcrCreditGrants,
} from '../../../../api/firebase/firestore/ocrCreditUsage/read';
import { CompanyDetailType } from '../../../../types/company';
import CustomSnackbar from '../../../uiParts/form/CustomSnackbar';
import CustomLoading from '../../../uiParts/form/CustomLoading';
import OcrCreditUsageHistoryModal from '../../../uiParts/modal/OcrCreditUsageHistoryModal';
import { companyPlan, companyStatus, paymentMethod, samlMode } from '../../../../constants/enum';
import { getSecuritySettingsConfig } from '../../../../api/firebase/firestore/security/read';
import { getPlanLabel } from '../../../../constants/plans';
import { getCreditRateByPlan } from '../../../../utils/userUsageLimit';
import {
  COMPANY_DETAIL_PROXY_LOGIN_BUTTON,
  COMPANY_DETAIL_CONTRACT_INFO_TITLE,
  COMPANY_DETAIL_CONTRACT_PLAN,
  COMPANY_DETAIL_CONTRACT_PLAN_CHANGE,
  COMPANY_DETAIL_MONTHLY_FEE,
  COMPANY_DETAIL_CONTRACT_START_DATE,
  COMPANY_DETAIL_NEXT_RENEWAL_DATE,
  COMPANY_DETAIL_CONTRACT_STATUS,
  COMPANY_DETAIL_USAGE_TITLE,
  COMPANY_DETAIL_USAGE_COUNT,
  COMPANY_DETAIL_MONTHLY_BUDGET_COST,
  COMPANY_DETAIL_CREDIT_COST,
  COMPANY_DETAIL_PROXY_OPERATION,
  COMPANY_DETAIL_OCR_CREDIT,
  COMPANY_DETAIL_OCR_CREDIT_ADD,
  COMPANY_DETAIL_COMPANY_INFO_TITLE,
  COMPANY_DETAIL_COMPANY_NAME,
  COMPANY_DETAIL_REPRESENTATIVE,
  COMPANY_DETAIL_PHONE_NUMBER,
  COMPANY_DETAIL_REGISTERED_DATE,
  COMPANY_DETAIL_UPDATED_DATE,
  COMPANY_DETAIL_CRM_ID,
  COMPANY_DETAIL_CRM_ID_SAVE_ERROR,
  COMPANY_DETAIL_CRM_ID_VALIDATION_ERROR,
  COMPANY_DETAIL_STATISTICS_TITLE,
  COMPANY_DETAIL_STAFF_COUNT,
  COMPANY_DETAIL_STAFF_COUNT_CHANGE,
  COMPANY_DETAIL_PATTERN_COUNT,
  COMPANY_DETAIL_PATTERN_COUNT_CHANGE,
  COMPANY_DETAIL_MASTER_RECORD_COUNT,
  COMPANY_DETAIL_MASTER_RECORD_COUNT_CHANGE,
  COMPANY_DETAIL_MASTER_RECORD_NO_LIMIT,
  COMPANY_DETAIL_MONTHLY_USAGE_AVG,
  COMPANY_DETAIL_ERROR_FETCH,
  COMPANY_DETAIL_ERROR_RETRY,
  COMPANY_DETAIL_SUSPENDED_ERROR,
  COMPANY_STATUS_INVITED,
  COMPANY_STATUS_ACTIVE,
  COMPANY_STATUS_SUSPENDED,
  WEBHOOK_FEATURE_DISABLED_SUCCESS,
  WEBHOOK_FEATURE_ENABLED_SUCCESS,
  SECURITY_ADDON_DISABLED_SUCCESS,
  SECURITY_ADDON_ENABLED_SUCCESS,
  IP_RESTRICTION_BYPASS_DISABLED_SUCCESS,
  IP_RESTRICTION_BYPASS_ENABLED_SUCCESS,
  COMPANY_DETAIL_OPTION_FEATURE_TITLE,
  COMPANY_DETAIL_OPTION_GROUP_FEATURE,
  COMPANY_DETAIL_OPTION_GROUP_SECURITY,
  COMPANY_DETAIL_FEATURE_MASTER,
  COMPANY_DETAIL_FEATURE_DICTIONARY,
  COMPANY_DETAIL_FEATURE_API,
  COMPANY_DETAIL_FEATURE_WEBHOOK,
  COMPANY_DETAIL_FEATURE_EMAIL_IMPORT,
  COMPANY_DETAIL_FEATURE_EXPORT_TEMPLATE,
  COMPANY_DETAIL_FEATURE_CUSTOM_FUNCTIONS,
  COMPANY_DETAIL_FEATURE_SECURITY_ADDON,
  COMPANY_DETAIL_FEATURE_IP_RESTRICTION_BYPASS,
  COMPANY_DETAIL_FEATURE_SAML_BYPASS,
  COMPANY_DETAIL_FEATURE_STRIPE,
  COMPANY_DETAIL_FEATURE_ENABLED,
  COMPANY_DETAIL_FEATURE_DISABLED,
} from '../../../../constants/labels';
import { ProxyLoginContext } from '../../../../contexts/ProxyLoginContext';
import { isOperatorAdmin, isOperatorStaff } from '../../../../utils/permission';
import { updateCompanyStatus } from '../../../../api/firebase/firestore/company/updateStatus';
import StatusChangeConfirmDialog from '../../../uiParts/modal/StatusChangeConfirmDialog';
import { suspendInvitedLabels } from '../../../../constants/suspendInvited';
import { useAdminPageAccessControl } from '../../../../hooks/useAdminPageAccessControl';
import ApiFeatureToggleDialog from './ApiFeatureToggleDialog';
import ProxyLoginConfirmDialog from '../../../uiParts/modal/ProxyLoginConfirmDialog';
import WebhookFeatureToggleDialog from './WebhookFeatureToggleDialog';
import SecurityAddonToggleDialog from './SecurityAddonToggleDialog';
import IpRestrictionBypassToggleDialog from './IpRestrictionBypassToggleDialog';
import SamlModeBypassToggleDialog from './SamlModeBypassToggleDialog';
import EmailImportFeatureToggleDialog from './EmailImportFeatureToggleDialog';
import FeatureToggleRow from './FeatureToggleRow';

const styles = {
  headerTitle: {
    ...boldXxl,
    color: gray100,
    '@media screen and (max-width:960px)': {
      ...spHeaderTitle,
    },
  },
  headerRightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
    },
  },
  contentContainer: {
    // 最下段のカード（今月の利用状況・統計情報）が画面下端に張り付かないよう、
    // 左右と同じ 24px の余白を下にも取る。SP は pageContent 側の paddingBottom に任せる。
    padding: '0 24px 24px 24px',
    width: '100%',
    boxSizing: 'border-box',
    '@media screen and (max-width:960px)': {
      padding: '0 16px',
    },
  },
  sectionCard: {
    backgroundColor: white,
    border: `1px solid ${gray40}`,
    borderRadius: '4px',
    padding: '24px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    '@media screen and (max-width:960px)': {
      padding: '16px',
    },
  },
  sectionTitle: {
    ...boldMd,
    color: gray100,
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    '&::before': {
      content: '""',
      display: 'inline-block',
      width: '4px',
      height: '20px',
      backgroundColor: orange50,
      marginRight: '8px',
    },
  },
  // オプション機能カード内のサブグループ見出し（#3193）
  groupTitle: {
    ...boldMd,
    color: gray80,
    textAlign: 'left',
    marginBottom: '4px',
    paddingBottom: '8px',
    borderBottom: `2px solid ${gray40}`,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: `1px solid ${gray40}`,
    '&:last-child': {
      borderBottom: 'none',
    },
    '@media screen and (max-width:960px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      position: 'relative',
    },
  },

  // 右上のボタンを絶対配置している行に付ける。
  // 絶対配置は行の padding box を基準にするため right: 0 の位置は動かないまま、
  // 値のテキストだけがボタンの手前で折り返すようになる。
  infoRowWithButton: {
    '@media screen and (max-width:960px)': {
      paddingRight: '96px',
    },
  },
  infoLabel: {
    ...regularSm,
    color: gray80,
    // 最長ラベル「メール取り込み機能」に合わせて値の開始位置を揃える
    minWidth: '140px',
    textAlign: 'left',
    flexShrink: 0,
    '@media screen and (max-width:960px)': {
      minWidth: 'auto',
      marginBottom: '4px',
    },
  },
  usageInfoLabel: {
    ...regularSm,
    color: gray80,
    minWidth: '160px',
    textAlign: 'left',
    flexShrink: 0,
    '@media screen and (max-width:960px)': {
      minWidth: 'auto',
      marginBottom: '4px',
    },
  },
  infoValue: {
    ...regularMd,
    color: gray100,
    flex: 1,
    textAlign: 'left',
    marginLeft: '24px',
    wordBreak: 'break-word',
    '@media screen and (max-width:960px)': {
      marginLeft: 0,
    },
  },
  outlineButton: {
    height: '32px',
    padding: '8px 16px',
    fontSize: '12px',
    color: orange50,
    border: `1px solid ${orange50}`,
    backgroundColor: white,
    '&:hover': {
      backgroundColor: 'rgba(255, 107, 53, 0.08)',
      border: `1px solid ${orange50}`,
    },
    '@media screen and (max-width:960px)': {
      position: 'absolute',
      top: '36px',
      right: 0,
    },
  },
  primaryButton: {
    ...primaryButtonBackgroundColor,
    height: '44px',
    padding: '16px',
  },
  statusDot: {
    display: 'inline-block',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    marginRight: '8px',
  },
  statisticsCardContainer: {
    display: 'flex',
    gap: '16px',
    marginTop: '16px',
  },
  statisticsCard: {
    flex: 1,
    backgroundColor: white,
    border: `1px solid ${gray40}`,
    borderRadius: '4px',
    padding: '16px',
    textAlign: 'center',
  },
  statisticsValue: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: orange50,
  },
  statisticsUnit: {
    fontSize: '14px',
    color: gray80,
    marginLeft: '4px',
  },
  statisticsLabel: {
    ...regularSm,
    color: gray80,
    marginTop: '8px',
    '@media screen and (max-width:960px)': {
      minHeight: '2.8em',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  statisticsLink: {
    ...regularSm,
    color: orange50,
    marginTop: '8px',
    cursor: 'pointer',
    textDecoration: 'underline',
    '&:hover': {
      opacity: 0.8,
    },
  },
  expiryLink: {
    ...regularSm,
    color: orange50,
    cursor: 'pointer',
    textDecoration: 'underline',
    marginLeft: '8px',
    '&:hover': {
      opacity: 0.8,
    },
  },
};

const CompanyDetail = (): ReactElement => {
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const { user, isChecking } = useAdminPageAccessControl('CompanyDetail', 'staff');
  const { startProxyLogin } = useContext(ProxyLoginContext);
  const [company, setCompany] = useState<CompanyDetailType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<boolean>(false);
  const { errorText, infoText, snackbarOpen, setErrorText, setInfoText, setSnackbarOpen } =
    useSnackbar();
  const [expiringCredit, setExpiringCredit] = useState<{
    expiresAt: Date;
    totalAmount: number;
  } | null>(null);
  const [hasCreditGrants, setHasCreditGrants] = useState<boolean>(false);
  const [usageHistoryModalOpen, setUsageHistoryModalOpen] = useState<boolean>(false);
  const [proxyLoginDialogOpen, setProxyLoginDialogOpen] = useState<boolean>(false);
  const [isCrmIdEditing, setIsCrmIdEditing] = useState<boolean>(false);
  const [crmIdValue, setCrmIdValue] = useState<string>('');
  const crmIdInputRef = useRef<HTMLInputElement>(null);
  const [stripeDialogOpen, setStripeDialogOpen] = useState<boolean>(false);
  const [stripeDialogCustomerId, setStripeDialogCustomerId] = useState<string>('');
  const [stripeDialogSubscriptionId, setStripeDialogSubscriptionId] = useState<string>('');
  const [stripeDialogError, setStripeDialogError] = useState<string>('');
  const [stripeDialogSubmitting, setStripeDialogSubmitting] = useState<boolean>(false);
  const [masterDialogOpen, setMasterDialogOpen] = useState<boolean>(false);
  const [masterDialogSubmitting, setMasterDialogSubmitting] = useState<boolean>(false);
  // モーダル内の編集中の値（保存ボタンで初めて Firestore に反映する）
  const [masterDialogEnabled, setMasterDialogEnabled] = useState<boolean>(false);
  const [masterDialogSelfUpload, setMasterDialogSelfUpload] = useState<boolean>(false);
  // 辞書変換機能の設定ダイアログ用の状態
  const [dictionaryDialogOpen, setDictionaryDialogOpen] = useState<boolean>(false);
  const [dictionaryDialogSubmitting, setDictionaryDialogSubmitting] = useState<boolean>(false);
  const [dictionaryDialogEnabled, setDictionaryDialogEnabled] = useState<boolean>(false);
  // 指定フォーマット出力（#3070）の有効/無効
  const [exportTemplateDialogOpen, setExportTemplateDialogOpen] = useState<boolean>(false);
  const [exportTemplateDialogSubmitting, setExportTemplateDialogSubmitting] =
    useState<boolean>(false);
  const [exportTemplateDialogEnabled, setExportTemplateDialogEnabled] = useState<boolean>(false);
  // カスタム関数（#3014/#3074）の有効/無効
  const [customFunctionsDialogOpen, setCustomFunctionsDialogOpen] = useState<boolean>(false);
  const [customFunctionsDialogSubmitting, setCustomFunctionsDialogSubmitting] =
    useState<boolean>(false);
  const [customFunctionsDialogEnabled, setCustomFunctionsDialogEnabled] = useState<boolean>(false);
  // 子パターン（#2967）の有効/無効
  const [apiDialogOpen, setApiDialogOpen] = useState<boolean>(false);
  const [webhookDialogOpen, setWebhookDialogOpen] = useState<boolean>(false);
  const [securityAddonDialogOpen, setSecurityAddonDialogOpen] = useState<boolean>(false);
  // IP制限 一時無効化（#2982・運営 admin のロックアウト救済）
  const [ipBypassDialogOpen, setIpBypassDialogOpen] = useState<boolean>(false);
  const [ipRestrictionDisabled, setIpRestrictionDisabled] = useState<boolean>(false);
  const [samlBypassDialogOpen, setSamlBypassDialogOpen] = useState<boolean>(false);
  const [samlModeBypass, setSamlModeBypass] = useState<boolean>(false);
  const [samlRequiredConfigured, setSamlRequiredConfigured] = useState<boolean>(false);
  const [emailImportDialogOpen, setEmailImportDialogOpen] = useState<boolean>(false);
  // 契約ステータス変更ダイアログ (#2802)
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState<boolean>(false);
  const [statusChanging, setStatusChanging] = useState<boolean>(false);

  const fetchCompanyDetail = async (): Promise<void> => {
    if (!companyId) {
      navigate('/404');
      return;
    }

    try {
      setLoading(true);
      setFetchError(false);
      const companyData = await getCompanyDetail(companyId);

      if (!companyData) {
        navigate('/404');
        return;
      }

      setCompany(companyData);

      // 有効期限情報・付与履歴の有無を取得（エラーでも企業情報は表示する）
      try {
        const [expiringCreditData, hasGrants] = await Promise.all([
          getEarliestExpiringCredit(companyId),
          hasOcrCreditGrants(companyId),
        ]);
        setExpiringCredit(expiringCreditData);
        setHasCreditGrants(hasGrants);
      } catch (expiryError) {
        Sentry.captureException(expiryError);
      }

      // SAML 必須バイパス状態を取得（#2983・エラーでも企業情報は表示する）
      try {
        const securityConfig = await getSecuritySettingsConfig(companyId);
        setSamlModeBypass(securityConfig?.saml_mode_bypass === true);
        setSamlRequiredConfigured(
          securityConfig?.saml?.enabled === true && securityConfig.saml.mode === samlMode.required,
        );
      } catch (securityError) {
        Sentry.captureException(securityError);
      }
    } catch {
      setFetchError(true);
      setErrorText(COMPANY_DETAIL_ERROR_FETCH);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isChecking || !user) {
      return;
    }

    fetchCompanyDetail();
  }, [user, isChecking, companyId]);

  // CRM IDの初期値を設定
  useEffect(() => {
    if (company) {
      setCrmIdValue(company.crm_id);
    }
  }, [company?.crm_id]);

  // IP制限 一時無効化の現在値を取得（アドオン有効企業のみ・#2982）
  useEffect(() => {
    if (!companyId || company?.has_security_addon !== true) {
      setIpRestrictionDisabled(false);
      return;
    }
    getSecuritySettingsConfig(companyId)
      .then(config => setIpRestrictionDisabled(config?.ip_restriction_disabled === true))
      .catch(() => setIpRestrictionDisabled(false));
  }, [companyId, company?.has_security_addon]);

  // CRM IDの編集アイコン押下
  const handleCrmIdEditClick = (): void => {
    setIsCrmIdEditing(true);
  };

  // CRM IDのフォーカス設定
  useEffect(() => {
    if (isCrmIdEditing && crmIdInputRef.current) {
      crmIdInputRef.current.focus();
    }
  }, [isCrmIdEditing]);

  // CRM IDのblur時保存処理
  const handleCrmIdBlur = async (): Promise<void> => {
    setIsCrmIdEditing(false);
    if (!companyId || !user) return;

    const trimmedValue = crmIdValue.trim();
    if (trimmedValue !== '' && !/^[a-zA-Z0-9]+$/.test(trimmedValue)) {
      setErrorText(COMPANY_DETAIL_CRM_ID_VALIDATION_ERROR);
      setSnackbarOpen(true);
      setCrmIdValue(company?.crm_id ?? '');
      return;
    }

    if (trimmedValue === (company?.crm_id ?? '')) {
      setCrmIdValue(trimmedValue);
      return;
    }

    try {
      await updateCompanyCrmId(companyId, trimmedValue, user.uid);
      const now = new Date();
      setCompany(prev => (prev ? { ...prev, crm_id: trimmedValue, updated_at: now } : prev));
      setCrmIdValue(trimmedValue);
    } catch (error) {
      Sentry.captureException(error);
      setErrorText(COMPANY_DETAIL_CRM_ID_SAVE_ERROR);
      setSnackbarOpen(true);
      setCrmIdValue(company?.crm_id ?? '');
    }
  };

  // マスタ管理機能モーダルの保存（enable_master / master_self_upload を一括で書き戻す）
  // ローカル state と現在値で子パターンがあるフィールドだけ Firestore に書き込む。
  const handleMasterDialogSave = async (): Promise<void> => {
    if (!companyId || !user || !company) return;
    const newEnabled = masterDialogEnabled;
    // enable_master が OFF の場合は master_self_upload は意味を持たないので false に揃える
    const newSelfUpload = newEnabled ? masterDialogSelfUpload : false;
    const enableChanged = newEnabled !== company.enable_master;
    const selfUploadChanged = newSelfUpload !== company.master_self_upload;
    if (!enableChanged && !selfUploadChanged) {
      setMasterDialogOpen(false);
      return;
    }

    try {
      setMasterDialogSubmitting(true);
      if (enableChanged) {
        await updateCompanyEnableMaster(companyId, newEnabled, user.uid);
      }
      if (selfUploadChanged) {
        await updateCompanyMasterSelfUpload(companyId, newSelfUpload, user.uid);
      }
      setCompany(prev =>
        prev ? { ...prev, enable_master: newEnabled, master_self_upload: newSelfUpload } : prev,
      );
      setMasterDialogOpen(false);
    } catch (error) {
      Sentry.captureException(error);
      setErrorText('設定の保存に失敗しました。');
      setSnackbarOpen(true);
    } finally {
      setMasterDialogSubmitting(false);
    }
  };

  // 辞書変換機能の設定を保存する（保存ボタン押下まで Firestore には反映されない）
  const handleDictionaryDialogSave = async (): Promise<void> => {
    if (!companyId || !user || !company) return;
    const newEnabled = dictionaryDialogEnabled;
    if (newEnabled === company.enable_dictionary) {
      setDictionaryDialogOpen(false);
      return;
    }

    try {
      setDictionaryDialogSubmitting(true);
      await updateCompanyEnableDictionary(companyId, newEnabled, user.uid);
      setCompany(prev => (prev ? { ...prev, enable_dictionary: newEnabled } : prev));
      setDictionaryDialogOpen(false);
    } catch (error) {
      Sentry.captureException(error);
      setErrorText('設定の保存に失敗しました。');
      setSnackbarOpen(true);
    } finally {
      setDictionaryDialogSubmitting(false);
    }
  };

  // 指定フォーマット出力の設定を保存する（保存ボタン押下まで Firestore には反映されない）
  const handleExportTemplateDialogSave = async (): Promise<void> => {
    if (!companyId || !user || !company) return;
    const newEnabled = exportTemplateDialogEnabled;
    if (newEnabled === (company.enable_export_template ?? false)) {
      setExportTemplateDialogOpen(false);
      return;
    }

    try {
      setExportTemplateDialogSubmitting(true);
      await updateCompanyEnableExportTemplate(companyId, newEnabled, user.uid);
      setCompany(prev => (prev ? { ...prev, enable_export_template: newEnabled } : prev));
      setExportTemplateDialogOpen(false);
    } catch (error) {
      Sentry.captureException(error);
      setErrorText('設定の保存に失敗しました。');
      setSnackbarOpen(true);
    } finally {
      setExportTemplateDialogSubmitting(false);
    }
  };

  // カスタム関数の設定を保存する（保存ボタン押下まで Firestore には反映されない）
  const handleCustomFunctionsDialogSave = async (): Promise<void> => {
    if (!companyId || !user || !company) return;
    const newEnabled = customFunctionsDialogEnabled;
    if (newEnabled === (company.enable_custom_functions ?? false)) {
      setCustomFunctionsDialogOpen(false);
      return;
    }

    try {
      setCustomFunctionsDialogSubmitting(true);
      await updateCompanyEnableCustomFunctions(companyId, newEnabled, user.uid);
      setCompany(prev => (prev ? { ...prev, enable_custom_functions: newEnabled } : prev));
      setCustomFunctionsDialogOpen(false);
    } catch (error) {
      Sentry.captureException(error);
      setErrorText('設定の保存に失敗しました。');
      setSnackbarOpen(true);
    } finally {
      setCustomFunctionsDialogSubmitting(false);
    }
  };

  // 子パターンの設定を保存する（保存ボタン押下まで Firestore には反映されない）

  // Stripe設定ダイアログを開く
  const handleStripeDialogOpen = (): void => {
    setStripeDialogCustomerId(company?.stripe_customer_id ?? '');
    setStripeDialogSubscriptionId(company?.stripe_subscription_id ?? '');
    setStripeDialogError('');
    setStripeDialogOpen(true);
  };

  // Stripe設定ダイアログのバリデーション
  const getStripeDialogValidationError = (): string | null => {
    const cid = stripeDialogCustomerId.trim();
    const sid = stripeDialogSubscriptionId.trim();
    if (cid !== '' && !/^cus_[a-zA-Z0-9]{1,60}$/.test(cid)) {
      return 'Customer IDはcus_で始まる英数字で入力してください。';
    }
    if (sid !== '' && !/^sub_[a-zA-Z0-9]{1,60}$/.test(sid)) {
      return 'Subscription IDはsub_で始まる英数字で入力してください。';
    }
    if ((cid !== '') !== (sid !== '')) {
      return 'Customer IDとSubscription IDは両方入力してください。';
    }
    return null;
  };

  // Stripe設定を保存
  const handleStripeDialogSave = async (): Promise<void> => {
    if (!companyId || !user) return;

    const validationError = getStripeDialogValidationError();
    if (validationError) {
      setStripeDialogError(validationError);
      return;
    }

    const cid = stripeDialogCustomerId.trim();
    const sid = stripeDialogSubscriptionId.trim();

    try {
      setStripeDialogSubmitting(true);
      await updateCompanyStripeIds(companyId, cid, sid, user.uid);
      const now = new Date();
      setCompany(prev =>
        prev
          ? { ...prev, stripe_customer_id: cid, stripe_subscription_id: sid, updated_at: now }
          : prev,
      );
      setStripeDialogOpen(false);
    } catch (error) {
      Sentry.captureException(error);
      setStripeDialogError('保存に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setStripeDialogSubmitting(false);
    }
  };

  const getPlanName = (plan: number, cycle: string | null, method: number): string => {
    const planName = getPlanLabel(plan);

    // スタンダード・プロの場合は支払いサイクル・支払い方法を表示
    if ((plan === companyPlan.standard || plan === companyPlan.pro) && cycle) {
      const cycleName = cycle === 'monthly' ? '月払い' : cycle === 'yearly' ? '年払い' : '';
      const methodName =
        method === paymentMethod.creditCard
          ? 'クレジットカード'
          : method === paymentMethod.invoice
            ? '請求書'
            : '';
      const details = [cycleName, methodName].filter(Boolean).join('・');
      return details ? `${planName}（${details}）` : planName;
    }

    return planName;
  };

  const getStatusInfo = (status: number): { label: string; color: string } => {
    switch (status) {
      case companyStatus.invited:
        return { label: COMPANY_STATUS_INVITED, color: yellow50 };
      case companyStatus.active:
        return { label: COMPANY_STATUS_ACTIVE, color: green50 };
      case companyStatus.suspended:
        return { label: COMPANY_STATUS_SUSPENDED, color: red50 };
      default:
        return { label: COMPANY_STATUS_ACTIVE, color: green50 };
    }
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return '-';
    const d = new Date(date);
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
  };

  // 有効期限表示テキストを生成
  // 有効なクレジットがある場合は有効期限・一部期限切れの文言を返し、
  // 残高0円・期限切れでも付与履歴があれば「クレジット履歴」ラベルを返して履歴モーダルへの導線を維持する
  const getExpiryDisplayText = (): string | null => {
    if (!company) {
      return null;
    }

    if (expiringCredit && company.ocr_credit_balance > 0) {
      // remaining_amountの合計がocr_credit_balanceを超えないようクランプ
      const displayAmount = Math.min(expiringCredit.totalAmount, company.ocr_credit_balance);

      if (company.ocr_credit_balance === displayAmount) {
        return `有効期限: ${formatDate(expiringCredit.expiresAt)}`;
      }

      return `うち${displayAmount.toLocaleString()}円が ${formatDate(expiringCredit.expiresAt)} に期限切れ`;
    }

    if (hasCreditGrants) {
      return 'クレジット履歴';
    }

    return null;
  };

  const handleProxyLoginClick = (): void => {
    if (company?.status === companyStatus.suspended) {
      setErrorText(COMPANY_DETAIL_SUSPENDED_ERROR);
      setSnackbarOpen(true);
      return;
    }

    setProxyLoginDialogOpen(true);
  };

  const handleProxyLoginConfirm = (): void => {
    if (!companyId || !company || !user || user.role === null || !isOperatorStaff(user.role))
      return;

    setProxyLoginDialogOpen(false);
    startProxyLogin(companyId, company.company_name, user.uid, user.role);
  };

  const handleUserLimitChangeClick = (): void => {
    if (company?.status === companyStatus.suspended) {
      setErrorText(COMPANY_DETAIL_SUSPENDED_ERROR);
      setSnackbarOpen(true);
      return;
    }
    navigate(`/admin/user_limit_change/${companyId}`);
  };

  const handlePatternLimitChangeClick = (): void => {
    if (company?.status === companyStatus.suspended) {
      setErrorText(COMPANY_DETAIL_SUSPENDED_ERROR);
      setSnackbarOpen(true);
      return;
    }
    navigate(`/admin/pattern_limit_change/${companyId}`);
  };

  const handleMasterRecordLimitChangeClick = (): void => {
    if (company?.status === companyStatus.suspended) {
      setErrorText(COMPANY_DETAIL_SUSPENDED_ERROR);
      setSnackbarOpen(true);
      return;
    }
    navigate(`/admin/master_record_limit_change/${companyId}`);
  };

  const handlePlanChangeClick = (): void => {
    if (company?.status === companyStatus.suspended) {
      setErrorText(COMPANY_DETAIL_SUSPENDED_ERROR);
      setSnackbarOpen(true);
      return;
    }
    navigate(`/admin/plan_change/${companyId}`);
  };

  const isAdmin = user && isOperatorAdmin(user.role);

  // 契約ステータス変更 (#2802)
  const handleStatusChangeClick = (): void => {
    setStatusChangeDialogOpen(true);
  };

  const handleStatusChangeClose = (): void => {
    if (statusChanging) return;
    setStatusChangeDialogOpen(false);
  };

  const handleStatusChangeConfirm = async (): Promise<void> => {
    if (!company || !companyId || !user) return;
    if (company.status !== companyStatus.invited && company.status !== companyStatus.suspended) {
      return;
    }
    const nextStatus =
      company.status === companyStatus.invited ? companyStatus.suspended : companyStatus.invited;

    setStatusChanging(true);
    try {
      await updateCompanyStatus(companyId, nextStatus, user.uid);
      setStatusChangeDialogOpen(false);
      setInfoText(suspendInvitedLabels.companyChangeSuccess);
      setSnackbarOpen(true);
      await fetchCompanyDetail();
    } catch (error) {
      Sentry.captureException(error);
      setErrorText(suspendInvitedLabels.companyChangeError);
      setSnackbarOpen(true);
    } finally {
      setStatusChanging(false);
    }
  };

  // ダイアログ文言の決定（現在ステータスに応じて切替）
  const statusDialogTitle =
    company?.status === companyStatus.suspended
      ? suspendInvitedLabels.companyRestoreDialogTitle
      : suspendInvitedLabels.companySuspendDialogTitle;
  const statusDialogBody =
    company?.status === companyStatus.suspended
      ? suspendInvitedLabels.companyRestoreDialogBody
      : suspendInvitedLabels.companySuspendDialogBody;

  const renderError = (): ReactElement => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <Typography sx={{ ...regularMd, color: gray80, marginBottom: '16px' }}>
        {COMPANY_DETAIL_ERROR_FETCH}
      </Typography>
      <Button onClick={fetchCompanyDetail} sx={styles.primaryButton}>
        <Typography sx={buttonTextIconLarge}>{COMPANY_DETAIL_ERROR_RETRY}</Typography>
      </Button>
    </Box>
  );

  const renderContent = (): ReactElement => {
    if (!company) return <></>;

    const statusInfo = getStatusInfo(company.status);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* 上段: 契約情報・会社情報 */}
        <Grid2 container spacing={3} sx={{ alignItems: 'stretch' }}>
          <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
            <Box sx={styles.sectionCard}>
              <Typography sx={styles.sectionTitle}>{COMPANY_DETAIL_CONTRACT_INFO_TITLE}</Typography>
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={[
                    styles.infoRow,
                    styles.infoRowWithButton,
                    { justifyContent: 'space-between' },
                  ]}
                >
                  <Typography sx={styles.infoLabel}>{COMPANY_DETAIL_CONTRACT_PLAN}</Typography>
                  <Typography sx={styles.infoValue}>
                    {getPlanName(company.plan, company.payment_cycle, company.payment_method)}
                  </Typography>
                  {isAdmin && (
                    <Button
                      sx={[
                        styles.outlineButton,
                        {
                          '@media screen and (max-width:960px)': {
                            top: '28px',
                          },
                        },
                      ]}
                      onClick={handlePlanChangeClick}
                    >
                      {COMPANY_DETAIL_CONTRACT_PLAN_CHANGE}
                    </Button>
                  )}
                </Box>
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.infoLabel}>{COMPANY_DETAIL_MONTHLY_FEE}</Typography>
                  <Typography sx={styles.infoValue}>
                    {company.monthly_fee.toLocaleString()}円
                  </Typography>
                </Box>
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.infoLabel}>
                    {COMPANY_DETAIL_CONTRACT_START_DATE}
                  </Typography>
                  <Typography sx={styles.infoValue}>
                    {formatDate(company.contract_start_date)}
                  </Typography>
                </Box>
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.infoLabel}>{COMPANY_DETAIL_NEXT_RENEWAL_DATE}</Typography>
                  <Box>
                    <Typography sx={styles.infoValue}>
                      {formatDate(company.next_renewal_date)}
                    </Typography>
                    {company.plan === companyPlan.free && company.next_renewal_date && (
                      <Typography sx={{ ...regularSm, color: gray80 }}>
                        ※ 変更前プランの有効期間終了日
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={[styles.infoRow, styles.infoRowWithButton]}>
                  <Typography sx={styles.infoLabel}>{COMPANY_DETAIL_CONTRACT_STATUS}</Typography>
                  <Box
                    sx={{
                      ...styles.infoValue,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '16px',
                    }}
                  >
                    <Typography component="span" sx={{ ...regularMd, color: gray100 }}>
                      <Box
                        component="span"
                        sx={[styles.statusDot, { backgroundColor: statusInfo.color }]}
                      />
                      {statusInfo.label}
                    </Typography>
                    {/* 契約ステータス変更ボタン #2802: 運営管理者のみ、invited / suspended のときのみ表示 */}
                    {isAdmin &&
                      (company.status === companyStatus.invited ||
                        company.status === companyStatus.suspended) && (
                        <Button
                          sx={styles.outlineButton}
                          onClick={handleStatusChangeClick}
                          disabled={statusChanging}
                        >
                          変更する
                        </Button>
                      )}
                  </Box>
                </Box>
                {/* Stripe ID設定は課金に直結するため契約情報に配置する（#3193） */}
                <Box
                  sx={[
                    styles.infoRow,
                    styles.infoRowWithButton,
                    { justifyContent: 'space-between' },
                  ]}
                >
                  <Typography sx={{ ...styles.infoLabel, display: 'flex', alignItems: 'center' }}>
                    {COMPANY_DETAIL_FEATURE_STRIPE}
                    {company.payment_method === paymentMethod.creditCard &&
                      (!company.stripe_customer_id || !company.stripe_subscription_id) && (
                        <Tooltip
                          title="クレジットカード払いの場合、Stripe IDの設定が必要です"
                          arrow
                        >
                          <ErrorOutline
                            sx={{
                              fontSize: '20px',
                              color: red50,
                              marginLeft: '6px',
                              cursor: 'pointer',
                            }}
                          />
                        </Tooltip>
                      )}
                  </Typography>
                  {/* Customer ID と Subscription ID は値カラム内で縦に並べ、
                      他の行と値の開始位置を揃える（#3193） */}
                  <Box
                    sx={{
                      ...styles.infoValue,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography sx={{ ...regularSm, color: gray80, flexShrink: 0 }}>
                        Customer ID:
                      </Typography>
                      <Typography
                        sx={{
                          ...regularMd,
                          color: company.stripe_customer_id ? gray100 : gray80,
                        }}
                      >
                        {company.stripe_customer_id || '未設定'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography sx={{ ...regularSm, color: gray80, flexShrink: 0 }}>
                        Subscription ID:
                      </Typography>
                      <Typography
                        sx={{
                          ...regularMd,
                          color: company.stripe_subscription_id ? gray100 : gray80,
                        }}
                      >
                        {company.stripe_subscription_id || '未設定'}
                      </Typography>
                    </Box>
                  </Box>
                  {isAdmin && (
                    <Button
                      sx={styles.outlineButton}
                      onClick={(): void => {
                        if (company?.payment_method === paymentMethod.invoice) {
                          setErrorText(
                            '請求書払いの企業です。Stripe IDの設定はクレジットカード払いに変更してから行ってください。',
                          );
                          setSnackbarOpen(true);
                          return;
                        }
                        handleStripeDialogOpen();
                      }}
                    >
                      変更する
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
            <Box sx={styles.sectionCard}>
              <Typography sx={styles.sectionTitle}>{COMPANY_DETAIL_COMPANY_INFO_TITLE}</Typography>
              <Box sx={{ flex: 1 }}>
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.infoLabel}>{COMPANY_DETAIL_COMPANY_NAME}</Typography>
                  <Typography sx={styles.infoValue}>
                    {company.company_name}（{company.company_id}）
                  </Typography>
                </Box>
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.infoLabel}>{COMPANY_DETAIL_REPRESENTATIVE}</Typography>
                  <Typography sx={styles.infoValue}>
                    {company.representative_name}
                    {company.representative_email && `（${company.representative_email}）`}
                  </Typography>
                </Box>
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.infoLabel}>{COMPANY_DETAIL_PHONE_NUMBER}</Typography>
                  <Typography sx={styles.infoValue}>{company.phone_number || '-'}</Typography>
                </Box>
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.infoLabel}>{COMPANY_DETAIL_REGISTERED_DATE}</Typography>
                  <Typography sx={styles.infoValue}>{formatDate(company.created_at)}</Typography>
                </Box>
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.infoLabel}>{COMPANY_DETAIL_UPDATED_DATE}</Typography>
                  <Typography sx={styles.infoValue}>{formatDate(company.updated_at)}</Typography>
                </Box>
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.infoLabel}>{COMPANY_DETAIL_CRM_ID}</Typography>
                  <Box
                    onClick={(): void => {
                      if (!isCrmIdEditing) {
                        handleCrmIdEditClick();
                      }
                    }}
                    sx={{
                      ...styles.infoValue,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: !isCrmIdEditing ? 'pointer' : 'default',
                    }}
                  >
                    <InputBase
                      value={crmIdValue}
                      onChange={(e): void => {
                        const value = e.target.value;
                        if (value !== '' && !/^[a-zA-Z0-9]+$/.test(value)) {
                          setErrorText(COMPANY_DETAIL_CRM_ID_VALIDATION_ERROR);
                          setSnackbarOpen(true);
                          return;
                        }
                        setCrmIdValue(value);
                      }}
                      onBlur={handleCrmIdBlur}
                      disabled={!isCrmIdEditing}
                      inputRef={crmIdInputRef}
                      placeholder="未設定"
                      inputProps={{ maxLength: 10 }}
                      sx={{
                        ...regularMd,
                        color: gray100,
                        width: '100px',
                        '& .MuiInputBase-input': {
                          padding: 0,
                        },
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: gray100,
                        },
                        ...(isCrmIdEditing && {
                          borderBottom: `1px solid ${orange50}`,
                        }),
                      }}
                    />
                    {!isCrmIdEditing && (
                      <IconButton
                        onClick={handleCrmIdEditClick}
                        size="small"
                        sx={{ padding: '4px' }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: '16px', color: gray80 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid2>
        </Grid2>

        {/* 中段: オプション機能（#3193・契約情報から分離してグループ表示） */}
        <Box sx={styles.sectionCard}>
          <Typography sx={styles.sectionTitle}>{COMPANY_DETAIL_OPTION_FEATURE_TITLE}</Typography>
          <Grid2 container columnSpacing={cardInnerColumnGutter} rowSpacing={3}>
            {/* 利用機能 */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Typography sx={styles.groupTitle}>{COMPANY_DETAIL_OPTION_GROUP_FEATURE}</Typography>
              <FeatureToggleRow
                label={COMPANY_DETAIL_FEATURE_MASTER}
                enabled={company.enable_master}
                statusText={
                  !company.enable_master
                    ? COMPANY_DETAIL_FEATURE_DISABLED
                    : company.master_self_upload
                      ? '有効（企業側もアップロード可）'
                      : '有効（運営のみアップロード可）'
                }
                showChangeButton={!!isAdmin}
                onChangeClick={(): void => {
                  // モーダルを開いた時点で現在の値をローカルにコピー
                  setMasterDialogEnabled(company.enable_master);
                  setMasterDialogSelfUpload(company.master_self_upload);
                  setMasterDialogOpen(true);
                }}
              />
              <FeatureToggleRow
                label={COMPANY_DETAIL_FEATURE_DICTIONARY}
                enabled={company.enable_dictionary}
                statusText={
                  company.enable_dictionary
                    ? COMPANY_DETAIL_FEATURE_ENABLED
                    : COMPANY_DETAIL_FEATURE_DISABLED
                }
                showChangeButton={!!isAdmin}
                onChangeClick={(): void => {
                  // モーダルを開いた時点で現在の値をローカルにコピー
                  setDictionaryDialogEnabled(company.enable_dictionary);
                  setDictionaryDialogOpen(true);
                }}
              />
              <FeatureToggleRow
                label={COMPANY_DETAIL_FEATURE_API}
                enabled={company.api_enabled === true}
                statusText={
                  company.api_enabled === true
                    ? COMPANY_DETAIL_FEATURE_ENABLED
                    : COMPANY_DETAIL_FEATURE_DISABLED
                }
                showChangeButton={!!isAdmin}
                onChangeClick={(): void => setApiDialogOpen(true)}
              />
              <FeatureToggleRow
                label={COMPANY_DETAIL_FEATURE_WEBHOOK}
                enabled={company.webhook_enabled === true}
                statusText={
                  company.webhook_enabled === true
                    ? COMPANY_DETAIL_FEATURE_ENABLED
                    : COMPANY_DETAIL_FEATURE_DISABLED
                }
                showChangeButton={!!isAdmin}
                onChangeClick={(): void => setWebhookDialogOpen(true)}
              />
              <FeatureToggleRow
                label={COMPANY_DETAIL_FEATURE_EMAIL_IMPORT}
                enabled={company.email_import_enabled === true}
                statusText={
                  company.email_import_enabled === true
                    ? COMPANY_DETAIL_FEATURE_ENABLED
                    : COMPANY_DETAIL_FEATURE_DISABLED
                }
                showChangeButton={!!isAdmin}
                onChangeClick={(): void => setEmailImportDialogOpen(true)}
              />
              <FeatureToggleRow
                label={COMPANY_DETAIL_FEATURE_EXPORT_TEMPLATE}
                enabled={company.enable_export_template === true}
                statusText={
                  company.enable_export_template === true
                    ? COMPANY_DETAIL_FEATURE_ENABLED
                    : COMPANY_DETAIL_FEATURE_DISABLED
                }
                showChangeButton={!!isAdmin}
                onChangeClick={(): void => {
                  // モーダルを開いた時点で現在の値をローカルにコピー
                  setExportTemplateDialogEnabled(company.enable_export_template ?? false);
                  setExportTemplateDialogOpen(true);
                }}
              />
              <FeatureToggleRow
                label={COMPANY_DETAIL_FEATURE_CUSTOM_FUNCTIONS}
                enabled={company.enable_custom_functions === true}
                statusText={
                  company.enable_custom_functions === true
                    ? COMPANY_DETAIL_FEATURE_ENABLED
                    : COMPANY_DETAIL_FEATURE_DISABLED
                }
                showChangeButton={!!isAdmin}
                onChangeClick={(): void => {
                  // モーダルを開いた時点で現在の値をローカルにコピー
                  setCustomFunctionsDialogEnabled(company.enable_custom_functions ?? false);
                  setCustomFunctionsDialogOpen(true);
                }}
              />
            </Grid2>

            {/* セキュリティ */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Typography sx={styles.groupTitle}>{COMPANY_DETAIL_OPTION_GROUP_SECURITY}</Typography>
              <FeatureToggleRow
                label={COMPANY_DETAIL_FEATURE_SECURITY_ADDON}
                enabled={company.has_security_addon === true}
                statusText={
                  company.has_security_addon === true
                    ? COMPANY_DETAIL_FEATURE_ENABLED
                    : COMPANY_DETAIL_FEATURE_DISABLED
                }
                showChangeButton={!!isAdmin}
                onChangeClick={(): void => setSecurityAddonDialogOpen(true)}
              />
              {/* IP制限 一時無効化はアドオン有効企業のみ表示（#2982） */}
              {company.has_security_addon === true && (
                <FeatureToggleRow
                  label={COMPANY_DETAIL_FEATURE_IP_RESTRICTION_BYPASS}
                  enabled={!ipRestrictionDisabled}
                  statusText={ipRestrictionDisabled ? '一時無効化中' : '通常（制限適用）'}
                  showChangeButton={!!isAdmin}
                  onChangeClick={(): void => setIpBypassDialogOpen(true)}
                />
              )}
              {/* SAML 必須モードのテナントのみ表示（ロックアウト救済・#2983） */}
              {samlRequiredConfigured && (
                <FeatureToggleRow
                  label={COMPANY_DETAIL_FEATURE_SAML_BYPASS}
                  enabled={!samlModeBypass}
                  statusText={
                    samlModeBypass ? 'バイパス中（従来ログイン許可）' : '通常（SAML 必須）'
                  }
                  showChangeButton={!!isAdmin}
                  onChangeClick={(): void => setSamlBypassDialogOpen(true)}
                />
              )}
            </Grid2>
          </Grid2>
        </Box>

        {/* 下段: 今月の利用状況・統計情報 */}
        <Grid2 container spacing={3} sx={{ alignItems: 'stretch' }}>
          <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
            <Box sx={styles.sectionCard}>
              <Typography sx={styles.sectionTitle}>{COMPANY_DETAIL_USAGE_TITLE}</Typography>
              <Box sx={{ flex: 1 }}>
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.usageInfoLabel}>{COMPANY_DETAIL_USAGE_COUNT}</Typography>
                  <Typography sx={styles.infoValue}>
                    {company.current_month_ocr_count}枚
                    {((): string => {
                      const creditRate = getCreditRateByPlan(company.plan);
                      const budgetCount = Math.min(
                        company.current_month_ocr_count,
                        Math.floor(company.ocr_monthly_budget / creditRate),
                      );
                      const creditCount = Math.max(
                        0,
                        company.current_month_ocr_count - budgetCount,
                      );
                      return `（月間枠 ${budgetCount}枚 / クレジット ${creditCount}枚）`;
                    })()}
                  </Typography>
                </Box>
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.usageInfoLabel}>
                    {COMPANY_DETAIL_MONTHLY_BUDGET_COST}
                  </Typography>
                  <Typography sx={styles.infoValue}>
                    {Math.min(
                      company.current_month_ocr_cost,
                      company.ocr_monthly_budget,
                    ).toLocaleString()}
                    円{' / '}
                    {company.ocr_monthly_budget.toLocaleString()}円
                  </Typography>
                </Box>
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.usageInfoLabel}>{COMPANY_DETAIL_CREDIT_COST}</Typography>
                  <Typography sx={styles.infoValue}>
                    {Math.max(
                      0,
                      company.current_month_ocr_cost - company.ocr_monthly_budget,
                    ).toLocaleString()}
                    円
                  </Typography>
                </Box>
                <Box
                  sx={[
                    styles.infoRow,
                    styles.infoRowWithButton,
                    { justifyContent: 'space-between' },
                  ]}
                >
                  <Typography sx={styles.usageInfoLabel}>{COMPANY_DETAIL_OCR_CREDIT}</Typography>
                  <Typography sx={styles.infoValue}>
                    {company.ocr_credit_balance.toLocaleString()}円
                    {getExpiryDisplayText() && (
                      <Typography
                        component="span"
                        sx={styles.expiryLink}
                        onClick={(): void => setUsageHistoryModalOpen(true)}
                      >
                        {getExpiryDisplayText()}
                      </Typography>
                    )}
                  </Typography>
                  {isAdmin && (
                    <Button
                      sx={styles.outlineButton}
                      onClick={(): void => {
                        if (company?.status === companyStatus.suspended) {
                          setErrorText(COMPANY_DETAIL_SUSPENDED_ERROR);
                          setSnackbarOpen(true);
                          return;
                        }
                        navigate(`/admin/ocr_credit_add/${companyId}`);
                      }}
                    >
                      {COMPANY_DETAIL_OCR_CREDIT_ADD}
                    </Button>
                  )}
                </Box>
                <Box sx={styles.infoRow}>
                  <Typography sx={styles.usageInfoLabel}>
                    {COMPANY_DETAIL_PROXY_OPERATION}
                  </Typography>
                  <Typography sx={styles.infoValue}>{company.proxy_operation_count}枚</Typography>
                </Box>
              </Box>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
            <Box sx={styles.sectionCard}>
              <Typography sx={styles.sectionTitle}>{COMPANY_DETAIL_STATISTICS_TITLE}</Typography>
              <Box sx={{ flex: 1 }}>
                <Box sx={styles.statisticsCardContainer}>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={[styles.statisticsCard, { flex: 1 }]}>
                      <Typography sx={styles.statisticsLabel}>
                        {COMPANY_DETAIL_STAFF_COUNT}
                      </Typography>
                      <Typography>
                        <Box component="span" sx={styles.statisticsValue}>
                          {company.current_user_count}
                        </Box>
                        <Box component="span" sx={styles.statisticsUnit}>
                          ／{company.user_limit}人
                        </Box>
                      </Typography>
                      {isAdmin && (
                        <Typography
                          sx={styles.statisticsLink}
                          onClick={(): void => handleUserLimitChangeClick()}
                        >
                          {COMPANY_DETAIL_STAFF_COUNT_CHANGE}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={[styles.statisticsCard, { flex: 1 }]}>
                      <Typography sx={styles.statisticsLabel}>
                        {COMPANY_DETAIL_PATTERN_COUNT}
                      </Typography>
                      <Typography>
                        <Box component="span" sx={styles.statisticsValue}>
                          {company.current_pattern_count}
                        </Box>
                        <Box component="span" sx={styles.statisticsUnit}>
                          ／{company.pattern_limit}件
                        </Box>
                      </Typography>
                      {isAdmin && (
                        <Typography
                          sx={styles.statisticsLink}
                          onClick={(): void => handlePatternLimitChangeClick()}
                        >
                          {COMPANY_DETAIL_PATTERN_COUNT_CHANGE}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {/* AIマスタ照合レコード数（#3288）。上限未設定の企業は「制限なし」と表示する */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={[styles.statisticsCard, { flex: 1 }]}>
                      <Typography sx={styles.statisticsLabel}>
                        {COMPANY_DETAIL_MASTER_RECORD_COUNT}
                      </Typography>
                      <Typography>
                        <Box component="span" sx={styles.statisticsValue}>
                          {company.current_master_record_count.toLocaleString()}
                        </Box>
                        <Box component="span" sx={styles.statisticsUnit}>
                          ／
                          {company.master_record_limit === undefined
                            ? COMPANY_DETAIL_MASTER_RECORD_NO_LIMIT
                            : `${company.master_record_limit.toLocaleString()}件`}
                        </Box>
                      </Typography>
                      {isAdmin && (
                        <Typography
                          sx={styles.statisticsLink}
                          onClick={(): void => handleMasterRecordLimitChangeClick()}
                        >
                          {COMPANY_DETAIL_MASTER_RECORD_COUNT_CHANGE}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={[styles.statisticsCard, { flex: 1 }]}>
                      <Typography sx={styles.statisticsLabel}>
                        {COMPANY_DETAIL_MONTHLY_USAGE_AVG}
                      </Typography>
                      <Typography>
                        <Box component="span" sx={styles.statisticsValue}>
                          {company.monthly_average_ocr_count}
                        </Box>
                        <Box component="span" sx={styles.statisticsUnit}>
                          枚
                        </Box>
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    );
  };

  // 認証・権限チェック中 or データ取得中
  if (isChecking || loading) {
    return (
      <Box sx={layoutStyles.pageWrapper}>
        <MetaTitle />
        <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
          <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
        </Box>
        <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
          <CustomLoading loading={true} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          fetchError && { minHeight: '100vh' },
          {
            '@media screen and (max-width:960px)': {
              paddingBottom: '24px',
            },
          },
        ]}
      >
        {!fetchError && (
          <Box sx={{ width: '100%' }}>
            <Box sx={layoutStyles.header}>
              <Typography sx={styles.headerTitle}>{company?.company_name}</Typography>
              <Box sx={styles.headerRightSection}>
                <Button sx={styles.primaryButton} onClick={(): void => handleProxyLoginClick()}>
                  <Typography sx={buttonTextIconLarge}>
                    {COMPANY_DETAIL_PROXY_LOGIN_BUTTON}
                  </Typography>
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={[styles.contentContainer, fetchError && { flex: 1 }]}>
          {fetchError ? renderError() : renderContent()}
        </Box>
      </Box>

      <CustomSnackbar
        text={errorText}
        infoText={infoText}
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
      />

      {/* 契約ステータス変更ダイアログ #2802 */}
      <StatusChangeConfirmDialog
        open={statusChangeDialogOpen}
        title={statusDialogTitle}
        body={statusDialogBody}
        processing={statusChanging}
        onClose={handleStatusChangeClose}
        onConfirm={handleStatusChangeConfirm}
      />

      <OcrCreditUsageHistoryModal
        open={usageHistoryModalOpen}
        onClose={(): void => setUsageHistoryModalOpen(false)}
        companyId={companyId ?? ''}
      />

      <ProxyLoginConfirmDialog
        open={proxyLoginDialogOpen}
        companyName={company?.company_name ?? ''}
        onClose={(): void => setProxyLoginDialogOpen(false)}
        onConfirm={handleProxyLoginConfirm}
      />

      <ApiFeatureToggleDialog
        open={apiDialogOpen}
        companyId={company?.company_id ?? ''}
        currentEnabled={company?.api_enabled === true}
        onClose={(): void => setApiDialogOpen(false)}
        onChanged={(after): void => {
          setCompany((prev): CompanyDetailType | null =>
            prev ? { ...prev, api_enabled: after } : prev,
          );
          setInfoText(after ? 'API機能を有効化しました' : 'API機能を無効化しました');
          setSnackbarOpen(true);
        }}
      />

      <WebhookFeatureToggleDialog
        open={webhookDialogOpen}
        companyId={company?.company_id ?? ''}
        currentEnabled={company?.webhook_enabled === true}
        onClose={(): void => setWebhookDialogOpen(false)}
        onChanged={(after): void => {
          setCompany((prev): CompanyDetailType | null =>
            prev ? { ...prev, webhook_enabled: after } : prev,
          );
          setInfoText(after ? WEBHOOK_FEATURE_ENABLED_SUCCESS : WEBHOOK_FEATURE_DISABLED_SUCCESS);
          setSnackbarOpen(true);
        }}
      />

      <EmailImportFeatureToggleDialog
        open={emailImportDialogOpen}
        companyId={company?.company_id ?? ''}
        currentEnabled={company?.email_import_enabled === true}
        onClose={(): void => setEmailImportDialogOpen(false)}
        onChanged={(after): void => {
          setCompany((prev): CompanyDetailType | null =>
            prev ? { ...prev, email_import_enabled: after } : prev,
          );
          setInfoText(
            after ? 'メール取り込み機能を有効化しました' : 'メール取り込み機能を無効化しました',
          );
          setSnackbarOpen(true);
        }}
      />

      <SecurityAddonToggleDialog
        open={securityAddonDialogOpen}
        companyId={company?.company_id ?? ''}
        currentEnabled={company?.has_security_addon === true}
        onClose={(): void => setSecurityAddonDialogOpen(false)}
        onChanged={(after): void => {
          setCompany((prev): CompanyDetailType | null =>
            prev ? { ...prev, has_security_addon: after } : prev,
          );
          setInfoText(after ? SECURITY_ADDON_ENABLED_SUCCESS : SECURITY_ADDON_DISABLED_SUCCESS);
          setSnackbarOpen(true);
        }}
      />

      <IpRestrictionBypassToggleDialog
        open={ipBypassDialogOpen}
        companyId={company?.company_id ?? ''}
        currentDisabled={ipRestrictionDisabled}
        onClose={(): void => setIpBypassDialogOpen(false)}
        onChanged={(afterDisabled): void => {
          setIpRestrictionDisabled(afterDisabled);
          setInfoText(
            afterDisabled
              ? IP_RESTRICTION_BYPASS_ENABLED_SUCCESS
              : IP_RESTRICTION_BYPASS_DISABLED_SUCCESS,
          );
          setSnackbarOpen(true);
        }}
      />

      <SamlModeBypassToggleDialog
        open={samlBypassDialogOpen}
        companyId={company?.company_id ?? ''}
        currentBypass={samlModeBypass}
        onClose={(): void => setSamlBypassDialogOpen(false)}
        onChanged={(after): void => {
          setSamlModeBypass(after);
          setInfoText(
            after
              ? 'SAML必須モードを一時バイパスしました（従来ログイン許可）。'
              : 'SAML必須モードを再適用しました。',
          );
          setSnackbarOpen(true);
        }}
      />

      {/* Stripe設定ダイアログ */}
      <Dialog
        open={stripeDialogOpen}
        onClose={(): void => setStripeDialogOpen(false)}
        sx={{
          '& .MuiPaper-root': {
            border: '1px solid' + gray40,
            boxSizing: 'border-box',
            margin: '16px',
            padding: '40px',
            width: '480px',
            maxWidth: '90vw',
            '@media screen and (max-width: 960px)': {
              padding: '32px 16px',
            },
          },
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '24px',
            '@media screen and (max-width:960px)': {
              marginBottom: '16px',
            },
          }}
        >
          <Typography
            sx={{
              ...regularXl,
              '@media screen and (max-width:960px)': {
                ...regularLg,
              },
            }}
          >
            Stripe ID設定
          </Typography>
          <IconButton onClick={(): void => setStripeDialogOpen(false)} sx={{ padding: 0 }}>
            <Close
              sx={{
                height: '32px',
                width: '32px',
                '@media screen and (max-width:960px)': {
                  height: '24px',
                  width: '24px',
                },
              }}
            />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <TextField
            label="Customer ID"
            value={stripeDialogCustomerId}
            onChange={(e): void => {
              setStripeDialogCustomerId(e.target.value);
              setStripeDialogError('');
            }}
            placeholder="cus_xxxxxxxxx"
            size="small"
            fullWidth
            inputProps={{ maxLength: 64 }}
          />
          <TextField
            label="Subscription ID"
            value={stripeDialogSubscriptionId}
            onChange={(e): void => {
              setStripeDialogSubscriptionId(e.target.value);
              setStripeDialogError('');
            }}
            placeholder="sub_xxxxxxxxx"
            size="small"
            fullWidth
            inputProps={{ maxLength: 64 }}
          />
          {stripeDialogError && (
            <Typography sx={{ ...regularSm, color: red50 }}>{stripeDialogError}</Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: '16px',
            marginTop: '32px',
            '@media screen and (max-width:960px)': {
              gap: '8px',
              marginTop: '20px',
            },
          }}
        >
          <Button
            sx={{
              ...tertiaryButtonBackgroundColor,
              height: '44px',
              width: 'calc(50% - 8px)',
              '@media screen and (max-width:960px)': {
                width: 'calc(50% - 4px)',
              },
            }}
            onClick={(): void => setStripeDialogOpen(false)}
            disabled={stripeDialogSubmitting}
          >
            <Typography sx={{ ...boldMd, color: gray80 }}>キャンセル</Typography>
          </Button>
          <Button
            sx={{
              height: '44px',
              width: 'calc(50% - 8px)',
              ...primaryButtonBackgroundColor,
            }}
            onClick={handleStripeDialogSave}
            disabled={stripeDialogSubmitting}
            variant="contained"
          >
            <Typography sx={{ ...boldMd, color: 'white' }}>
              {stripeDialogSubmitting ? '保存中...' : '保存する'}
            </Typography>
          </Button>
        </Box>
      </Dialog>
      {/* マスタ管理機能 変更ダイアログ */}
      <Dialog
        open={masterDialogOpen}
        onClose={(): void => setMasterDialogOpen(false)}
        sx={{
          '& .MuiPaper-root': {
            border: '1px solid' + gray40,
            boxSizing: 'border-box',
            margin: '16px',
            padding: '40px',
            width: '480px',
            maxWidth: '90vw',
            '@media screen and (max-width: 960px)': {
              padding: '32px 16px',
            },
          },
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '24px',
          }}
        >
          <Typography sx={regularXl}>マスタ管理機能の設定</Typography>
          <IconButton onClick={(): void => setMasterDialogOpen(false)} sx={{ padding: 0 }}>
            <Close sx={{ height: '32px', width: '32px' }} />
          </IconButton>
        </Box>

        {/* マスタ照合機能 ON/OFF（保存ボタン押下まで Firestore には反映されない） */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 0',
            borderBottom: `1px solid ${gray40}`,
          }}
        >
          <Box>
            <Typography sx={{ ...regularMd, color: gray100 }}>マスタ照合機能</Typography>
            <Typography sx={{ ...regularSm, color: gray80 }}>
              OFFにすると企業ユーザーのマスタ管理画面が利用できなくなります。
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Switch
              checked={masterDialogEnabled}
              onChange={(): void => setMasterDialogEnabled(!masterDialogEnabled)}
              disabled={masterDialogSubmitting}
            />
            <Typography sx={{ ...regularSm, color: gray80, minWidth: '32px' }}>
              {masterDialogEnabled ? 'ON' : 'OFF'}
            </Typography>
          </Box>
        </Box>

        {/* 企業ユーザーによるアップロード許可（マスタ照合機能 が ON のときのみ操作可） */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 0',
            opacity: masterDialogEnabled ? 1 : 0.5,
          }}
        >
          <Box>
            <Typography sx={{ ...regularMd, color: gray100 }}>
              企業ユーザーによるアップロード
            </Typography>
            <Typography sx={{ ...regularSm, color: gray80 }}>
              OFFにすると運営のみがマスタCSVをアップロード・更新・削除できます。
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Switch
              checked={masterDialogEnabled && masterDialogSelfUpload}
              onChange={(): void => setMasterDialogSelfUpload(!masterDialogSelfUpload)}
              disabled={!masterDialogEnabled || masterDialogSubmitting}
            />
            <Typography sx={{ ...regularSm, color: gray80, minWidth: '32px' }}>
              {masterDialogEnabled && masterDialogSelfUpload ? 'ON' : 'OFF'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: '16px', marginTop: '24px' }}>
          <Button
            sx={{
              ...tertiaryButtonBackgroundColor,
              height: '44px',
              flex: 1,
            }}
            onClick={(): void => setMasterDialogOpen(false)}
            disabled={masterDialogSubmitting}
          >
            <Typography sx={{ ...boldMd, color: gray80 }}>キャンセル</Typography>
          </Button>
          <Button
            sx={{
              ...primaryButtonBackgroundColor,
              height: '44px',
              flex: 1,
            }}
            onClick={(): Promise<void> => handleMasterDialogSave()}
            disabled={masterDialogSubmitting}
          >
            <Typography sx={{ ...boldMd, color: 'white' }}>
              {masterDialogSubmitting ? '保存中...' : '保存'}
            </Typography>
          </Button>
        </Box>
      </Dialog>

      {/* 辞書変換機能の設定ダイアログ */}
      <Dialog
        open={dictionaryDialogOpen}
        onClose={(): void => setDictionaryDialogOpen(false)}
        sx={{
          '& .MuiPaper-root': {
            border: '1px solid' + gray40,
            boxSizing: 'border-box',
            margin: '16px',
            padding: '40px',
            width: '480px',
            maxWidth: '90vw',
            '@media screen and (max-width: 960px)': {
              padding: '32px 16px',
            },
          },
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '24px',
          }}
        >
          <Typography sx={regularXl}>辞書変換機能の設定</Typography>
          <IconButton onClick={(): void => setDictionaryDialogOpen(false)} sx={{ padding: 0 }}>
            <Close sx={{ height: '32px', width: '32px' }} />
          </IconButton>
        </Box>

        {/* 辞書変換機能 ON/OFF（保存ボタン押下まで Firestore には反映されない） */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 0',
          }}
        >
          <Box>
            <Typography sx={{ ...regularMd, color: gray100 }}>辞書変換機能</Typography>
            <Typography sx={{ ...regularSm, color: gray80 }}>
              OFFにすると企業ユーザーの辞書データ管理画面が利用できなくなります。
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Switch
              checked={dictionaryDialogEnabled}
              onChange={(): void => setDictionaryDialogEnabled(!dictionaryDialogEnabled)}
              disabled={dictionaryDialogSubmitting}
            />
            <Typography sx={{ ...regularSm, color: gray80, minWidth: '32px' }}>
              {dictionaryDialogEnabled ? 'ON' : 'OFF'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: '16px', marginTop: '24px' }}>
          <Button
            sx={{
              ...tertiaryButtonBackgroundColor,
              height: '44px',
              flex: 1,
            }}
            onClick={(): void => setDictionaryDialogOpen(false)}
            disabled={dictionaryDialogSubmitting}
          >
            <Typography sx={{ ...boldMd, color: gray80 }}>キャンセル</Typography>
          </Button>
          <Button
            sx={{
              ...primaryButtonBackgroundColor,
              height: '44px',
              flex: 1,
            }}
            onClick={(): Promise<void> => handleDictionaryDialogSave()}
            disabled={dictionaryDialogSubmitting}
          >
            <Typography sx={{ ...boldMd, color: 'white' }}>
              {dictionaryDialogSubmitting ? '保存中...' : '保存'}
            </Typography>
          </Button>
        </Box>
      </Dialog>

      {/* 指定フォーマット出力の設定ダイアログ（#3070） */}
      <Dialog
        open={exportTemplateDialogOpen}
        onClose={(): void => setExportTemplateDialogOpen(false)}
        sx={{
          '& .MuiPaper-root': {
            border: '1px solid' + gray40,
            boxSizing: 'border-box',
            margin: '16px',
            padding: '40px',
            width: '480px',
            maxWidth: '90vw',
            '@media screen and (max-width: 960px)': {
              padding: '32px 16px',
            },
          },
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '24px',
          }}
        >
          <Typography sx={regularXl}>指定フォーマット出力の設定</Typography>
          <IconButton onClick={(): void => setExportTemplateDialogOpen(false)} sx={{ padding: 0 }}>
            <Close sx={{ height: '32px', width: '32px' }} />
          </IconButton>
        </Box>

        {/* ON/OFF（保存ボタン押下まで Firestore には反映されない） */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 0',
          }}
        >
          <Box>
            <Typography sx={{ ...regularMd, color: gray100 }}>指定フォーマット出力</Typography>
            <Typography sx={{ ...regularSm, color: gray80 }}>
              ONにすると、読取パターンに書式付きExcelを登録して、その書式へ流し込んだ出力ができます。
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Switch
              checked={exportTemplateDialogEnabled}
              onChange={(): void => setExportTemplateDialogEnabled(!exportTemplateDialogEnabled)}
              disabled={exportTemplateDialogSubmitting}
            />
            <Typography sx={{ ...regularSm, color: gray80, minWidth: '32px' }}>
              {exportTemplateDialogEnabled ? 'ON' : 'OFF'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: '16px', marginTop: '24px' }}>
          <Button
            sx={{
              ...tertiaryButtonBackgroundColor,
              height: '44px',
              flex: 1,
            }}
            onClick={(): void => setExportTemplateDialogOpen(false)}
            disabled={exportTemplateDialogSubmitting}
          >
            <Typography sx={{ ...boldMd, color: gray80 }}>キャンセル</Typography>
          </Button>
          <Button
            sx={{
              ...primaryButtonBackgroundColor,
              height: '44px',
              flex: 1,
            }}
            onClick={(): Promise<void> => handleExportTemplateDialogSave()}
            disabled={exportTemplateDialogSubmitting}
          >
            <Typography sx={{ ...boldMd, color: 'white' }}>
              {exportTemplateDialogSubmitting ? '保存中...' : '保存'}
            </Typography>
          </Button>
        </Box>
      </Dialog>

      {/* カスタム関数の設定モーダル（#3014/#3074） */}
      <Dialog
        open={customFunctionsDialogOpen}
        onClose={(): void => setCustomFunctionsDialogOpen(false)}
        sx={{
          '& .MuiPaper-root': {
            border: '1px solid' + gray40,
            boxSizing: 'border-box',
            margin: '16px',
            padding: '40px',
            width: '480px',
            maxWidth: '90vw',
            '@media screen and (max-width: 960px)': {
              padding: '32px 16px',
            },
          },
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '24px',
          }}
        >
          <Typography sx={regularXl}>カスタム関数の設定</Typography>
          <IconButton onClick={(): void => setCustomFunctionsDialogOpen(false)} sx={{ padding: 0 }}>
            <Close sx={{ height: '32px', width: '32px' }} />
          </IconButton>
        </Box>

        {/* ON/OFF（保存ボタン押下まで Firestore には反映されない） */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 0',
          }}
        >
          <Box>
            <Typography sx={{ ...regularMd, color: gray100 }}>カスタム関数</Typography>
            <Typography sx={{ ...regularSm, color: gray80 }}>
              ONにすると、この企業向けに実装されたカスタム処理がOCR結果に適用されます。カスタム処理が未実装の企業では、ONにしても動作は変わりません。
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Switch
              checked={customFunctionsDialogEnabled}
              onChange={(): void => setCustomFunctionsDialogEnabled(!customFunctionsDialogEnabled)}
              disabled={customFunctionsDialogSubmitting}
            />
            <Typography sx={{ ...regularSm, color: gray80, minWidth: '32px' }}>
              {customFunctionsDialogEnabled ? 'ON' : 'OFF'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: '16px', marginTop: '24px' }}>
          <Button
            sx={{
              ...tertiaryButtonBackgroundColor,
              height: '44px',
              flex: 1,
            }}
            onClick={(): void => setCustomFunctionsDialogOpen(false)}
            disabled={customFunctionsDialogSubmitting}
          >
            <Typography sx={{ ...boldMd, color: gray80 }}>キャンセル</Typography>
          </Button>
          <Button
            sx={{
              ...primaryButtonBackgroundColor,
              height: '44px',
              flex: 1,
            }}
            onClick={(): Promise<void> => handleCustomFunctionsDialogSave()}
            disabled={customFunctionsDialogSubmitting}
          >
            <Typography sx={{ ...boldMd, color: 'white' }}>
              {customFunctionsDialogSubmitting ? '保存中...' : '保存'}
            </Typography>
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default CompanyDetail;
